import type { ProgramNonPendingUserRsvp, ProgramUserRsvp } from 'models';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type EventRsvpLabels = {
  getAcceptedSectionTitle: ({ count }: { count: number }) => string;
  getButtonActionLabel: ({ action }: { action: ProgramNonPendingUserRsvp }) => string;
  getCountLabel: ({ count, rsvp }: { count: number; rsvp: ProgramUserRsvp }) => string;
  getDropdownActionLabel: ({ action }: { action: ProgramNonPendingUserRsvp }) => string;
  getInviteesAcceptedLabel: ({ formattedInvitees }: { formattedInvitees: string }) => string;
  getPastStatusLabel: ({ rsvp }: { rsvp: ProgramUserRsvp }) => string;
  getStatusListLabel: ({ rsvp }: { rsvp: ProgramUserRsvp }) => string;
};

export const useEventRsvpLabels = (): EventRsvpLabels => {
  const { t } = useTranslation();

  const getButtonActionLabel = useCallback(
    ({ action }: { action: ProgramNonPendingUserRsvp }) => {
      switch (action) {
        case 'going':
          return t('Accept');
        case 'not_going':
          return t('Decline');
      }
    },
    [t],
  );

  const getDropdownActionLabel = useCallback(
    ({ action }: { action: ProgramNonPendingUserRsvp }) => {
      switch (action) {
        case 'going':
          return t('Accepted');
        case 'not_going':
          return t('Declined');
      }
    },
    [t],
  );

  const getPastStatusLabel = useCallback(
    ({ rsvp }: { rsvp: ProgramUserRsvp }) => {
      switch (rsvp) {
        case 'going':
          return t('Accepted');
        case 'not_going':
          return t('Declined');
        case 'pending':
          return t('You didn’t respond');
      }
    },
    [t],
  );

  const getStatusListLabel = useCallback(
    ({ rsvp }: { rsvp: ProgramUserRsvp }) => {
      switch (rsvp) {
        case 'going':
          return t('Accepted');
        case 'not_going':
          return t('Declined');
        case 'pending':
          return t('Pending');
      }
    },
    [t],
  );

  const getCountLabel = useCallback(
    ({ count, rsvp }: { count: number; rsvp: ProgramUserRsvp }) => {
      switch (rsvp) {
        case 'going':
          return t('accepted', { count });
        case 'not_going':
          return t('declined', { count });
        case 'pending':
          return t('pending', { count });
      }
    },
    [t],
  );

  const getInviteesAcceptedLabel = useCallback(
    ({ formattedInvitees }: { formattedInvitees: string }) =>
      t('{{invitees}} accepted', { invitees: formattedInvitees }),
    [t],
  );

  const getAcceptedSectionTitle = useCallback(
    ({ count }: { count: number }) => t('Accepted ({{count}})', { count }),
    [t],
  );

  return useMemo(
    () => ({
      getAcceptedSectionTitle,
      getButtonActionLabel,
      getCountLabel,
      getDropdownActionLabel,
      getInviteesAcceptedLabel,
      getPastStatusLabel,
      getStatusListLabel,
    }),
    [
      getAcceptedSectionTitle,
      getButtonActionLabel,
      getCountLabel,
      getDropdownActionLabel,
      getInviteesAcceptedLabel,
      getPastStatusLabel,
      getStatusListLabel,
    ],
  );
};
